import React, { useEffect, useState } from 'react';
import {
    Box,
    FormControl,
    Grid,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";
import { addDays, subDays } from "date-fns";
import { DateRange, DesktopDateRangePicker } from "@mui/x-date-pickers-pro";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { MuiTextFieldProps } from "@mui/x-date-pickers/internals";

interface DateRangeSelectorProps {
    title: string;
    value: DateRange<Date>;
    error?: boolean;
    helperText?: React.ReactNode;
    disabled?: boolean;
    onChange: (value: DateRange<Date>) => void;
}

export default function DateRangeSelector(props: DateRangeSelectorProps): JSX.Element {
    const { title, value, error, helperText, disabled = false, onChange } = props;

    // Calculate date for the "preset" selectors and update the appropriate state values
    const calcDates = (
        event: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        const currentDate = new Date();
        let fromDate: Date | null = currentDate;
        let toDate: Date | null = currentDate;

        setPreset(event.target.value);

        switch (event.target.value) {
            case "clear": {
                fromDate = null;
                toDate = null;
                break;
            }
            case "last7": {
                fromDate = subDays(currentDate, 7);
                break;
            }
            case "yesterday": {
                fromDate = subDays(currentDate, 1);
                toDate = fromDate;
                break;
            }
            case "tomorrow": {
                fromDate = addDays(currentDate, 1);
                toDate = fromDate;
                break;
            }
            case "next7": {
                toDate = addDays(currentDate, 7);
                break;
            }
        }

        onChange([fromDate, toDate]);
    }

    const handleDateRangeChange = (newRange: DateRange<Date>) => {
        onChange(newRange);
        setPreset("");
    };

    const [preset, setPreset] = useState("");

    useEffect(() => {
        if (value[0] === null && value[1] === null) {
            setPreset("");
        }
    }, [value])

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container sx={{ marginTop: 1, spacing: 1, justifyContent: "flex-start" }}>
                <Grid item xs={12} sx={{textAlign: "left"}}>
                    <Typography variant={"subtitle1"}>{title}</Typography>
                </Grid>
                <Grid item xs={4} sx={{justifyContent: "flex-start", }}>
                    <FormControl sx={{ minWidth: 120 }}>
                        <TextField
                            label="Preset"
                            margin="dense"
                            select
                            disabled={disabled}
                            value={preset}
                            variant="standard"
                            onChange={calcDates}
                        >
                            <MenuItem value="clear">Not set</MenuItem>
                            <MenuItem value="last7">Last 7 days</MenuItem>
                            <MenuItem value="yesterday">Yesterday</MenuItem>
                            <MenuItem value="today">Today</MenuItem>
                            <MenuItem value="next7">Next 7 days</MenuItem>
                        </TextField>
                    </FormControl>
                </Grid>
                <Grid item xs={8}>
                    <DesktopDateRangePicker
                        inputFormat="dd/MM/yyyy"
                        value={value}
                        onChange={handleDateRangeChange}
                        renderInput={(startProps: MuiTextFieldProps, endProps: MuiTextFieldProps) => (
                            <React.Fragment>
                                <TextField
                                    {...startProps}
                                    variant="standard"
                                    label="Start date"
                                    error={error}
                                    helperText={helperText}
                                    inputProps={{
                                        ...startProps.inputProps,
                                        autoComplete: "off",
                                    }}
                                />
                                <Box sx={{mx: 2}}> to </Box>
                                <TextField
                                    {...endProps}
                                    variant="standard"
                                    label="End date"
                                    error={error}
                                    inputProps={{
                                        ...endProps.inputProps,
                                        autoComplete: "off",
                                    }}
                                />
                            </React.Fragment>
                        )}
                    />
                </Grid>
            </Grid>
        </LocalizationProvider>
    );
}
